import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import { Input, Button } from '../common';
import { theme } from '../../styles/theme';
import { PDFDocument, rgb } from 'pdf-lib';

const ChecklistTable = ({ onEdit, refreshTrigger }) => {
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('revision_number');
  const [sortDirection, setSortDirection] = useState('asc');

  useEffect(() => {
    fetchChecklists();
  }, [sortField, sortDirection, refreshTrigger]);

  const fetchChecklists = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('audit_checklist')
        .select(`
          id,
          revision_number,
          title,
          requirement,
          checklist_questions,
          auditor_actions,
          audit_sampling_methods,
          guidance,
          description_short,
          description_long,
          created_at,
          created_by,
          updated_by
        `)
        .order(sortField, { ascending: sortDirection === 'asc' });

      if (search) {
        query = query.or(
          `title.ilike.%${search}%,` +
          `revision_number.ilike.%${search}%,` +
          `description_short.ilike.%${search}%`
        );
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;
      
      // Parse checklist_questions for each checklist
      const parsedData = data?.map(checklist => ({
        ...checklist,
        checklist_questions: checklist.checklist_questions 
          ? (typeof checklist.checklist_questions === 'string'
              ? JSON.parse(checklist.checklist_questions)
              : checklist.checklist_questions)
          : []
      })) || [];

      setChecklists(parsedData);
      setError(null);
    } catch (error) {
      console.error('Error fetching checklists:', error);
      setError('Failed to load checklists');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this checklist?')) {
      return;
    }

    try {
      const { error: deleteError } = await supabase
        .from('audit_checklist')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;
      fetchChecklists();
    } catch (error) {
      console.error('Error deleting checklist:', error);
      setError('Failed to delete checklist');
    }
  };

  const generatePDF = async (checklist) => {
    try {
      console.log('Generating PDF for checklist:', checklist);

      // Parse checklist_questions if it's a string
      const parsedChecklist = {
        ...checklist,
        checklist_questions: checklist.checklist_questions 
          ? (typeof checklist.checklist_questions === 'string' 
              ? JSON.parse(checklist.checklist_questions) 
              : checklist.checklist_questions)
          : []
      };

      console.log('Parsed checklist:', parsedChecklist);

      if (!parsedChecklist.checklist_questions || !Array.isArray(parsedChecklist.checklist_questions)) {
        throw new Error('Invalid checklist questions format');
      }

      const pdfDoc = await PDFDocument.create();
      const page = pdfDoc.addPage([600, 800]);
      const { width, height } = page.getSize();

      // Add title
      page.drawText('Audit Checklist', {
        x: 50,
        y: height - 50,
        size: 20,
        color: rgb(0, 0, 0)
      });

      // Add checklist number
      page.drawText(`Checklist Number: ${parsedChecklist.revision_number}`, {
        x: 50,
        y: height - 80,
        size: 12
      });

      // Add requirement
      page.drawText('Requirement:', {
        x: 50,
        y: height - 120,
        size: 12,
        color: rgb(0.4, 0.4, 0.4)
      });
      page.drawText(parsedChecklist.requirement || '', {
        x: 50,
        y: height - 140,
        size: 12
      });

      // Add questions table
      let yOffset = height - 200;
      if (parsedChecklist.checklist_questions && parsedChecklist.checklist_questions.length > 0) {
        parsedChecklist.checklist_questions.forEach((q, index) => {
          if (yOffset < 100) {
            // Add new page if needed
            page = pdfDoc.addPage([600, 800]);
            yOffset = height - 50;
          }

          page.drawText(`${index + 1}. ${q.question}`, {
            x: 50,
            y: yOffset,
            size: 10
          });
          yOffset -= 20;
          page.drawText(`Compliance: ${q.compliance_verified ? 'Yes' : 'No'}`, {
            x: 70,
            y: yOffset,
            size: 10
          });
          yOffset -= 20;
          page.drawText(`Evidence/Observations: ${q.evidence_observations || ''}`, {
            x: 70,
            y: yOffset,
            size: 10
          });
          yOffset -= 30;
        });
      }

      // Add remaining sections
      const sections = [
        { title: 'Auditor Actions', content: parsedChecklist.auditor_actions },
        { title: 'Audit Sampling Methods', content: parsedChecklist.audit_sampling_methods },
        { title: 'Guidance', content: parsedChecklist.guidance }
      ];

      sections.forEach(section => {
        if (yOffset < 100) {
          // Add new page if needed
          page = pdfDoc.addPage([600, 800]);
          yOffset = height - 50;
        }
        page.drawText(section.title, {
          x: 50,
          y: yOffset,
          size: 12,
          color: rgb(0.4, 0.4, 0.4)
        });
        yOffset -= 20;
        page.drawText(section.content || '', {
          x: 50,
          y: yOffset,
          size: 10
        });
        yOffset -= 40;
      });

      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `checklist-${parsedChecklist.revision_number}.pdf`;
      link.click();
    } catch (error) {
      console.error('Error generating PDF:', error);
      setError('Failed to generate PDF');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <div className="w-72">
          <Input
            placeholder="Search checklists..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('revision_number')}
              >
                Number {sortField === 'revision_number' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('title')}
              >
                Title {sortField === 'title' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Description
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('created_at')}
              >
                Created {sortField === 'created_at' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {checklists.map((checklist) => (
              <tr key={checklist.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {checklist.revision_number}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {checklist.title}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {checklist.description_short}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(checklist.created_at).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div className="flex space-x-2">
                    <Button
                      variant="secondary"
                      onClick={() => onEdit(checklist)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => generatePDF(checklist)}
                    >
                      Export PDF
                    </Button>
                    <button
                      onClick={() => handleDelete(checklist.id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {checklists.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No checklists found
        </div>
      )}
    </div>
  );
};

export default ChecklistTable;
